import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import type { ContentSnapshotV2 } from "../../runtime/content/ContentSnapshotV2";
import type { DevicePreset, EditorToRuntime, RuntimeToEditor } from "../../runtime/editor-bridge/messages";
import type { TestPlayerContext } from "../../runtime/player/TestPlayerContext";
import type { RuntimePreviewContext } from "../../runtime/ui/types";
import type { RuntimeElementPatch } from "../../runtime/editor-bridge/messages";

const replyTypes = new Set<RuntimeToEditor["type"]>(["READY", "ELEMENT_SELECTED", "ELEMENT_RECTS", "ACTION_TRIGGERED", "RUNTIME_ERROR"]);

function isRuntimeReply(value: unknown): value is RuntimeToEditor {
  return Boolean(value && typeof value === "object" && "type" in value && replyTypes.has((value as RuntimeToEditor).type));
}

export type RuntimePreviewHandle = {
  patchElement: (id: string, patch: RuntimeElementPatch) => void;
  openScreen: (screenKey: string, context?: RuntimePreviewContext) => void;
};

export const RuntimePreviewFrame = forwardRef<RuntimePreviewHandle, {
  snapshot: ContentSnapshotV2;
  screenKey: string;
  context?: RuntimePreviewContext;
  device?: DevicePreset;
  editMode?: boolean;
  testPlayer?: TestPlayerContext;
  onMessage?: (message: RuntimeToEditor) => void;
  onReady?: () => void;
}>(function RuntimePreviewFrame({ snapshot, screenKey, context, device = { width: 1080, height: 2340 }, editMode = true, testPlayer, onMessage, onReady }, ref) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [ready, setReady] = useState(false);
  const [lastSelection, setLastSelection] = useState<string>();
  const [lastAction, setLastAction] = useState<string>();
  const [rectCount, setRectCount] = useState(0);
  const [error, setError] = useState<string>();
  const contextKey = JSON.stringify(context ?? {});
  const stableContext = useMemo(() => context, [contextKey]);

  const send = useCallback((message: EditorToRuntime) => {
    frameRef.current?.contentWindow?.postMessage(message, window.location.origin);
  }, []);
  useImperativeHandle(ref, () => ({
    patchElement: (id, patch) => send({ type: "PATCH_ELEMENT", id, patch }),
    openScreen: (key, nextContext) => send({ type: "OPEN_SCREEN", screenKey: key, context: nextContext }),
  }), [send]);

  useEffect(() => {
    const receive = (event: MessageEvent<unknown>) => {
      if (event.origin !== window.location.origin || event.source !== frameRef.current?.contentWindow || !isRuntimeReply(event.data)) return;
      const message = event.data;
      if (message.type === "READY") { setReady(true); onReady?.(); }
      if (message.type === "ELEMENT_SELECTED") setLastSelection(message.id);
      if (message.type === "ACTION_TRIGGERED") setLastAction(message.action.type === "open_game_content" ? "Open Game Content" : message.action.type);
      if (message.type === "ELEMENT_RECTS") setRectCount(message.rects.length);
      if (message.type === "RUNTIME_ERROR") setError(message.message);
      onMessage?.(message);
    };
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, [onMessage, onReady]);

  useEffect(() => {
    if (ready) send({ type: "LOAD_DRAFT_CONTENT", payload: snapshot, testPlayer });
  }, [ready, send, snapshot, testPlayer]);
  useEffect(() => {
    if (ready) send({ type: "OPEN_SCREEN", screenKey, context: stableContext });
  }, [contextKey, ready, screenKey, send, stableContext]);
  useEffect(() => {
    if (ready) send({ type: "SET_DEVICE_PRESET", ...device });
  }, [device.height, device.safeBottom, device.safeTop, device.width, ready, send]);
  useEffect(() => {
    if (ready) send({ type: "SET_EDIT_MODE", enabled: editMode });
  }, [editMode, ready, send]);

  return <div className="runtime-preview-frame" data-testid="runtime-preview-frame" data-bridge-status={ready ? "ready" : "connecting"} data-runtime-error={error ? "true" : undefined} data-last-selection={lastSelection} data-last-action={lastAction} data-element-rect-count={rectCount}>
    <iframe ref={frameRef} src="/player?editorPreview=1" title="Actual game runtime preview" />
    <div className="runtime-bridge-status"><span>{ready ? "Runtime ready" : "Connecting runtime…"}</span>{lastSelection && <span>Selected: {lastSelection}</span>}{lastAction && <span>Action: {lastAction}</span>}</div>
    {error && <p className="access-error" role="alert">{error}</p>}
  </div>;
});
